import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AuthStatus, useAuth } from "../../hooks/useAuth"
import { useInvitation } from "../../queries/useInvitation"
import { useSignup } from "../../queries/useSignup"
import { useWelcomeStore } from "../../stores/welcomeStore"
import { InvitationPostBody } from "../../types/types"
import { validateBirthday, validatePwd } from "../../utils/validations"

interface IProps {}

const Signup = (props: IProps) => {
  const navigate = useNavigate()
  const { study, studyId, token } = useWelcomeStore()
  const { data: invitation } = useInvitation(studyId, !token)
  const { status } = useAuth()

  const { mutateAsync, isPending: signupIsPending, isError } = useSignup()
  const [inputs, setInputs] = useState({
    email: "",
    password: "",
    name: "",
    surname: "",
    birthday: "",
    terms: true,
    recommended: study?.recommended ?? false
  })
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  )
  const [birthdayError, setBirthdayError] = useState<string | undefined>(
    undefined
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e?.target?.name
    const value = e?.target?.value
    setInputs((values) => ({ ...values, [name]: value }))
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const error = validatePwd(e?.target?.value)
    setPasswordError(error)
    handleChange(e)
  }

  const handleBirthdayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const error = validateBirthday(e?.target?.value)
    setBirthdayError(error)
    handleChange(e)
  }

  const handleCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e?.target?.name
    const value = e?.target?.checked
    setInputs((values) => ({ ...values, [name]: value }))
  }

  const signupForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const email = inputs["email"]
    const password = inputs["password"]
    const name = inputs["name"]
    const surname = inputs["surname"]
    const birthday = inputs["birthday"]
    const recommended = inputs["recommended"]

    const patientInvitation: InvitationPostBody = {
      patient: {
        invitation_token: token || invitation?.invitation_token || "",
        email,
        password,
        name,
        surname,
        birthday,
        recommended
      }
    }
    mutateAsync(patientInvitation)
  }

  const checkInputsFilled = () => {
    const areFilled = Object.values(inputs).every((value) => value !== "")
    return areFilled && inputs["terms"]
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  useEffect(() => {
    if (status === AuthStatus.Authenticated) navigate("/")
  }, [status, navigate])

  return (
    <div className="flex items-center justify-center bg-cortexx-100 w-screen h-screen">
      <div className="bg-cortexx-50 w-full md:w-2/3 h-screen">
        <div className="flex items-center justify-center my-4">
          <div className="bg-cortexx-50">
            <img
              src={
                study
                  ? study?.administrative_information.logo_url
                  : "/images/spineDAO-logo.svg"
              }
              style={{ height: "80px" }}
              alt="RP Logo"
              loading="lazy"
            />
          </div>
        </div>
        <div className="flex items-center justify-center my-4">
          <div className="flex-row md:w-1/2 px-3">
            <h1 className="font-semibold text-xl md:text-2xl text-cortexx-900 text-center">
              Inscription
            </h1>
            <h2 className="font-semibold md:font-normal text-sm md:text-lg text-cortexx-900 text-center mt-4">
              Renseignez vos informations pour créer votre espace
            </h2>
            <h2 className="font-extralight text-sm text-cortexx-900 text-center mt-2">
              Vous pourrez retrouver vos informations et vos résultats dans
              votre espace, votre chirurgien aura accès à ces données pour
              préparer votre rendez-vous.
            </h2>
          </div>
        </div>
        <form className="flex flex-col items-center m-6" onSubmit={signupForm}>
          <div className="flex flex-col items-left justify-center mb-4">
            <h2 className="font-semibold text-lg text-cortexx-900">prénom</h2>
            <input
              className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none capitalize"
              name="surname"
              placeholder="prénom"
              onChange={handleChange}
            />
            <h2 className="font-semibold text-lg text-cortexx-900">nom</h2>
            <input
              className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none capitalize"
              name="name"
              placeholder="nom"
              onChange={handleChange}
            />
            <h2 className="font-semibold text-lg text-cortexx-900">
              date de naissance
            </h2>
            <input
              className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none"
              name="birthday"
              type="date"
              placeholder="date de naissance"
              onChange={handleBirthdayChange}
            />
            {birthdayError && (
              <div className="text-cortexx-800 text-xs w-72 text-justify">
                {birthdayError}
              </div>
            )}
            <h2 className="font-semibold text-lg text-cortexx-900">email</h2>
            <input
              className="border-2 border-slate-300 rounded-xl w-72 py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none"
              name="email"
              type="email"
              placeholder="email"
              onChange={handleChange}
            />
            <h2 className="font-semibold text-lg text-cortexx-900">
              mot de passe
            </h2>
            <div className="flex border-2 border-slate-300 rounded-xl mb-2 leading-tight items-center w-72 ">
              <input
                className="py-2 px-3 text-gray-700 w-64 rounded-xl  focus:outline-none"
                name="password"
                type={passwordVisible ? "text" : "password"}
                placeholder="mot de passe"
                onChange={handlePasswordChange}
              />
              <img
                src={
                  passwordVisible ? "/images/eye_unlock.svg" : "/images/eye.svg"
                }
                style={{ height: "22px" }}
                alt="close"
                loading="lazy"
                onClick={togglePasswordVisibility}
              />
            </div>
            {passwordError && (
              <div className="text-cortexx-800 text-xs w-72 text-justify">
                {passwordError}
              </div>
            )}
            <a href="/login">
              <p className="font-extralight text-sm text-cortexx-1000 text-end mt-1">
                Déjà inscrit ? Connectez-vous
              </p>
            </a>

            <div className="flex justify-end items-end mt-4">
              {signupIsPending ? (
                <button className="bg-cortexx-100 text-cortexx-500 font-semibold py-2 px-4 rounded-2xl">
                  <div className="flex-row flex items-end justify-end mx-2 my-1">
                    <p>Inscription</p>
                    <div className="ml-2">
                      <img
                        src="/images/fade-stagger-circles.svg"
                        style={{ height: "20px" }}
                        alt="close"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </button>
              ) : !checkInputsFilled() ? (
                <button
                  className="bg-cortexx-550 hover:opacity-60 rounded-2xl py-3 px-6 text-cortexx-50 font-semibold"
                  type="submit"
                  disabled
                >
                  <p>Inscription</p>
                </button>
              ) : (
                <button
                  className="bg-cortexx-500 hover:opacity-60 rounded-2xl py-3 px-6 text-cortexx-50 font-semibold"
                  type="submit"
                >
                  <p>Inscription</p>
                </button>
              )}
            </div>
            {isError && (
              <div className="text-center text-red-500 text-sm mt-2">
                L'inscription a échouée, veuillez réessayer
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Signup
